import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { armarMailRadar } from "../../constants/plantillasRadar";
import { MESES_ES } from "../../utils/alertasRecurrencia";
import { seleccionarFotosRadar } from "../../utils/fotosRadar";
import { getPaisCode } from "../../utils/getPaisCode";
import { connectGoogle, crearBorradorRadar } from "../../utils/googleOAuth";
import { log } from "../../utils/log";
import { estamparLogoEnImagen } from "../../utils/logoOperacion";
import { registrarBorradorRadar } from "../../utils/radarBorradores";
import styles from "./stylesEscribirRadarModal.module.scss";

// Modal "Escribir" del Radar Comercial —
// docs/decisiones/2026-09-26-radar-mail-pagina-diseno.md §3, §9, §11-§13. Molde estándar del
// proyecto (`reactstrap` Modal/ModalHeader/ModalBody/ModalFooter, mismo patrón que
// `AgendarMovimientoModal`).
//
// SIN "agregar contacto" en esta fase (decisión explícita del prompt de Fase B): la lista de
// contactos es de solo selección (checkbox) sobre lo que ya trae `radarContactos.js` — sumar
// contactos nuevos y ofrecer registrarlos queda para el proyecto de Contactos aparte (§9 del
// diseño documenta por qué: el CRM hoy reescribe la tabla relacional completa en cada guardado,
// tocar eso de paso sería un riesgo mayor al que pide esta tarea).
//
// "Abrir en Gmail" NUNCA cambia el estado de la alerta — `escritoCliente`/`escritoAgencia` solo
// llegan, más tarde, cuando `gmail-sync` confirma un mail SALIENTE real (`radarMailSaliente.js`).
// El insert en
// `radar_borradores` de acá es puramente informativo (trazabilidad de intención) y nunca bloquea
// la creación del borrador si falla (10.170, ver `registrarBorradorRadar`).
//
// BORRADOR POR API DE GMAIL (2026-09-26, reemplaza las DOS vías por link que existieron antes):
//   - `?view=cm&...` (redacción "en línea"): Ignacio verificó en su Gmail real que NO trae la firma
//     predeterminada de la cuenta ni el estilo de texto predeterminado (Trebuchet en su caso).
//   - `mailto:` armado a mano (`https://mail.google.com/mail/?extsrc=mailto&url=...`): mismo
//     problema — Ignacio probó esta vía también y tampoco trae firma/tipografía reales.
// Ninguna de las dos podía llevar la firma real del vendedor porque un link no autentica nada del
// lado de Google — por eso el borrador pasa a crearse server-side vía `users.drafts.create` (edge
// function `radar-borrador`), que sí actúa CON el token OAuth del vendedor y puede leer su firma
// (`users.settings.sendAs`) y aplicar la tipografía institucional en el HTML del cuerpo.
//
// Deep link al borrador creado: `https://mail.google.com/mail/u/0/#drafts?compose=<draftId>` —
// VERIFICADO por Ignacio en la Parada 1 (2026-09-26, borrador real con su firma "Wecast -
// Ignacio" en Trebuchet azul): abre directo en modo edición. La otra variante probada
// (`#drafts/<threadId>`) queda descartada — documentada acá por si algún día `draftId` deja de
// venir en la respuesta y hay que volver a una alternativa.
//
// Fotos como ADJUNTOS (2026-09-27, corrección de Ignacio tras la prueba real — "en el cuerpo
// rompen el mail"): al abrir, se piden a `seleccionarFotosRadar` (galería, hasta 3, país-safe) y
// cada una se pasa por `estamparLogoEnImagen` (mismo logo/posición/tamaño que la descarga de
// Galería, `src/utils/logoOperacion.js`, 10.1) — el resultado (data URL JPEG) es lo que se
// muestra en "Adjuntos" y lo que viaja como adjunto real al crear el borrador (nunca inline en el
// cuerpo). El vendedor puede quitar cualquiera antes de abrir Gmail (`fotosQuitadas`, un Set de
// `empresaNorm`).
//
// Mismo tamaño/calidad que la descarga de Galería (2026-09-27, segunda corrección — Ignacio
// comparó el mail contra su descarga real de Galería y salían distintos): se pasa la foto ORIGINAL
// (`f.url`, nunca la transformación de Storage `?width=1200` — esa transformación, pedida sin
// `height`, deja el alto en su valor original en vez de escalarlo, verificado con datos reales —
// ver comentario de `logoOperacion.js`) y NINGÚN override de `maxAncho`/`calidad`: se usan los
// defaults de `estamparLogoEnImagen` (`Infinity`/calidad del navegador), IDÉNTICOS a los que usa
// `Galeria/index.js#downloadImage` — dos superficies, mismo resultado en bytes (10.1). El primer
// intento (`maxAncho:1200, calidad:0.85`) arreglaba la deformación pero seguía comprimiendo de
// más — se retiró entero.
//
// Un solo click, un solo borrador (2026-09-27, corrección de Ignacio — la primera prueba real
// creó DOS borradores a las 2:03pm por un doble click antes del re-render): `creandoRef` es un
// candado SÍNCRONO revisado al entrar a `crearBorrador`, además del `disabled` del botón — el
// estado de React se actualiza en el siguiente render, pero el ref se lee/escribe en el mismo tick,
// así que un doble click no puede colar una segunda invocación mientras la primera sigue en vuelo.
//
// Link vacío — RESUELTO (2026-09-27, verificado por Ignacio en su Gmail real con 4 formatos de
// link distintos, sobre DOS borradores reales creados por la API — uno con 3 fotos adjuntas, uno
// sin fotos): `#drafts?compose=<draftId>` y `#drafts?compose=<message.id>` abren vacíos (el
// formato que "funcionaba" en la Parada 1 era, en rigor, un borrador SIN adjuntos — con adjuntos
// nunca funcionó, ver historial de esta sección más abajo en git blame). El formato que SÍ abre el
// borrador completo, con fotos, es `#drafts/<message.id>` (el mismo `threadId` que Gmail devuelve
// junto al `draftId` — Parada 1 lo había probado y descartado, pero esa prueba fue SIN adjuntos y
// puede haber sido coincidencia). Con `authuser=<email del vendedor>` antepuesto (para no abrir la
// cuenta de Google equivocada si el navegador tiene varias sesiones), confirmado igual de bien
// sobre un tercer borrador de prueba real.
//
// Formato final: `https://mail.google.com/mail/?authuser=<email>#drafts/<message.id>` — `message.id`
// (no `draftId`) es la clave; `radarBorradorRadar`/`radar-borrador` ya lo devolvía (`messageId`),
// no hizo falta tocar la edge function. `window.open` sincrónico justo después del `await`, como
// siempre — el problema nunca fue LA MECÁNICA de apertura, era el formato de la URL en sí.
const urlBorrador = (email, messageId) =>
  `https://mail.google.com/mail/?authuser=${encodeURIComponent(email || "")}#drafts/${messageId}`;

export const EscribirRadarModal = ({
  isOpen,
  onClose,
  lado, // 'cliente' | 'agencia'
  alerta, // fila del Radar: empresaDisplay, empresaNorm, mesObjetivo, anoObjetivo, tipo, campana,
  // edificiosTotales, clientId
  contactos, // [{usuarioId, nombre, apellido, email, emailValido, cargo}] — filtrados a con-email
  // NO-VACÍO; `emailValido` (CAMBIO 1, caso Samsung 2026-09-27) puede venir en `false` (formato
  // inválido, ej. "samsung.com") — ver `src/utils/validarEmail.js`, esos SIGUEN llegando acá para
  // mostrarse en rojo, nunca se descartan en silencio.
  conteoRed, // { operativos, instalacion, piloto } de hoy (getConteoRedPorEstado) — null si no cargó
  hayAgenciaConEmail, // solo relevante si lado==='cliente' — dispara el recordatorio no bloqueante
  pais,
  usuarioId,
  emailVendedor, // usuarios.email del vendedor logueado (useAuthContext().correo) — carpeta de Borradores
  nombreVendedor, // usuarios.nombre del vendedor logueado — bloque 9 "Saludos,"
}) => {
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [estado, setEstado] = useState("idle"); // idle | creando | reauth | error
  const [fotosDisponibles, setFotosDisponibles] = useState([]); // {empresaNorm, marca, url, dataUrlEstampada, filename}
  const [fotosQuitadas, setFotosQuitadas] = useState(() => new Set()); // empresaNorm quitados por el vendedor
  const creandoRef = useRef(false); // candado síncrono, ver comentario de arriba

  const prefijoOperacion = pais === "Perú" ? "Visiona" : "Wecast";

  // Al abrir, todos los contactos marcados por defecto (§3.2: "todos los contactos del lado van
  // en Para"), se piden las fotos de galería para esta empresa/categoría y se estampa el logo de
  // la operación sobre cada una (Canvas del navegador — por eso pasa acá, no en el edge function).
  useEffect(() => {
    if (isOpen) {
      // Solo contactos con email de formato VÁLIDO entran tildados por defecto (§3.2 sigue
      // valiendo para esos) — uno con email inválido ("samsung.com", caso Samsung 2026-09-27)
      // nunca puede seleccionarse, ver el checkbox `disabled` más abajo.
      setSeleccionados(new Set(contactos.filter((c) => c.emailValido).map((c) => c.usuarioId)));
      setEstado("idle");
      setFotosQuitadas(new Set());
      setFotosDisponibles([]);
      creandoRef.current = false;
      if (alerta?.empresaDisplay) {
        seleccionarFotosRadar({
          pais,
          clienteIdDestinatario: alerta.clientId ?? null,
          empresaDestinatario: alerta.empresaDisplay,
        })
          .then(async (r) => {
            const fotos = r.fotos || [];
            const estampadas = await Promise.all(
              fotos.map(async (f) => {
                try {
                  const dataUrlEstampada = await estamparLogoEnImagen({ url: f.url, pais });
                  const filename = `${prefijoOperacion}_${(f.marca || "foto").replace(/\s+/g, "")}.jpg`;
                  return { ...f, dataUrlEstampada, filename };
                } catch (err) {
                  log.error("EscribirRadarModal.estamparLogoEnImagen", err);
                  return null; // esta foto puntual se descarta, no bloquea el resto (10.170)
                }
              })
            );
            setFotosDisponibles(estampadas.filter(Boolean));
          })
          .catch((err) => {
            log.error("EscribirRadarModal.seleccionarFotosRadar", err);
            setFotosDisponibles([]); // sale sin fotos, sin bloquear el modal (10.170)
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, alerta?.clientId, lado]);

  const fotosVisibles = useMemo(
    () => fotosDisponibles.filter((f) => !fotosQuitadas.has(f.empresaNorm)),
    [fotosDisponibles, fotosQuitadas]
  );

  const quitarFoto = (empresaNorm) => {
    setFotosQuitadas((prev) => new Set(prev).add(empresaNorm));
  };

  const mes = MESES_ES[alerta?.mesObjetivo] || "";

  // La vista previa SÍ recalcula al tildar/destildar contactos y al quitar fotos — el saludo
  // (singular/plural) y el bloque de fotos dependen de ambos.
  const nombresSeleccionados = useMemo(
    () => contactos.filter((c) => seleccionados.has(c.usuarioId)).map((c) => c.nombre),
    [contactos, seleccionados]
  );

  // CAMBIO 3 (2026-09-27, caso Samsung): el paralelo ya no lo decide "la agencia tiene contacto
  // con email" (`hayAgenciaConEmail`, que sigue viva SOLO para el recordatorio no bloqueante de
  // abajo) sino "ya se le escribió al OTRO lado" — en los dos sentidos, con la fuente ya calculada
  // por `calcularAlertasRecurrencia` (`alerta.escritoCliente`/`escritoAgencia`).
  const yaEscribioOtroLado = lado === "cliente" ? !!alerta?.escritoAgencia : !!alerta?.escritoCliente;

  // CAMBIO 1c — al menos un contacto de este lado con email de formato válido (mismo criterio que
  // decide si algo puede terminar tildado, ver `emailValido` de `radarContactos.js`).
  const hayAlgunEmailValido = contactos.some((c) => c.emailValido);

  const { asunto, cuerpoHtml } = useMemo(
    () =>
      armarMailRadar({
        lado,
        tipo: alerta?.tipo,
        empresa: alerta?.empresaDisplay,
        campana: alerta?.campana,
        mes,
        pais,
        nombresDestinatarios:
          nombresSeleccionados.length > 0
            ? nombresSeleccionados
            : contactos.filter((c) => c.emailValido).map((c) => c.nombre),
        nombreVendedor,
        conteoRed: conteoRed ?? null,
        yaEscribioOtroLado,
        hayFotos: fotosVisibles.length > 0,
      }),
    [
      lado,
      alerta,
      mes,
      pais,
      nombresSeleccionados,
      nombreVendedor,
      contactos,
      conteoRed,
      yaEscribioOtroLado,
      fotosVisibles.length,
    ]
  );

  const toggleContacto = (usuarioId2) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(usuarioId2)) next.delete(usuarioId2);
      else next.add(usuarioId2);
      return next;
    });
  };

  const crearBorrador = async () => {
    if (creandoRef.current) return; // candado síncrono — ver comentario de arriba del componente
    // `emailValido` es un guard defensivo (10.177): el checkbox de un contacto inválido ya viene
    // `disabled` y nunca entra a `seleccionados`, pero un email inválido no debe poder viajar a
    // `radar-borrador` bajo ninguna circunstancia (CAMBIO 1d, caso Samsung 2026-09-27).
    const marcados = contactos.filter((c) => seleccionados.has(c.usuarioId) && c.emailValido);
    if (marcados.length === 0) return;
    creandoRef.current = true;

    setEstado("creando");
    try {
      const resultado = await crearBorradorRadar({
        usuario_id: usuarioId,
        pais: getPaisCode(pais),
        pais_nombre: pais,
        lado,
        client_id: alerta.clientId,
        empresa_norm: alerta.empresaNorm,
        mes: alerta.mesObjetivo,
        ano: alerta.anoObjetivo,
        to: marcados.map((c) => c.email).join(","),
        subject: asunto,
        html: cuerpoHtml,
        fotos: fotosVisibles.map((f) => ({
          marca: f.marca,
          filename: f.filename,
          contentType: "image/jpeg",
          base64: f.dataUrlEstampada.split(",")[1] || "",
        })),
      });

      // Log de diagnóstico (2026-09-27 — Ignacio pidió loguear qué devuelve la edge function):
      // visible en la consola del navegador en cada borrador, no solo cuando algo falla.
      // eslint-disable-next-line no-console
      console.log("[Radar] Respuesta de radar-borrador:", resultado);

      if (!resultado?.messageId) {
        // Nunca debería pasar — Gmail siempre devuelve `message.id` en un create exitoso (10.177:
        // se avisa fuerte, nunca en silencio). Sin `messageId` no hay a qué borrador apuntar — se
        // abre la carpeta de Borradores (nunca un `#drafts/undefined`).
        log.error("EscribirRadarModal.crearBorrador", { mensaje: "Respuesta sin messageId", resultado });
        toast.error("El borrador se creó pero no se pudo armar el link directo — buscalo en Gmail > Borradores.");
      }

      registrarBorradorRadar({
        pais,
        empresaNorm: alerta.empresaNorm,
        mes: alerta.mesObjetivo,
        ano: alerta.anoObjetivo,
        lado,
        clientId: alerta.clientId,
        usuarioId,
      });

      const urlDestino = resultado?.messageId
        ? urlBorrador(emailVendedor, resultado.messageId)
        : `https://mail.google.com/mail/?authuser=${encodeURIComponent(emailVendedor || "")}#drafts`;
      // eslint-disable-next-line no-console
      console.log("[Radar] Abriendo:", urlDestino, "— confirmado por Gmail:", resultado?.draftConfirmado);
      if (resultado?.draftConfirmado === false) {
        toast("El link puede tardar unos segundos en mostrar el borrador — ya quedó guardado en Gmail > Borradores.", {
          icon: "⏳",
        });
      }
      window.open(urlDestino, "_blank", "noopener,noreferrer");
      onClose();
    } catch (err) {
      if (err.code === "REAUTH") {
        setEstado("reauth");
      } else {
        log.error("EscribirRadarModal.crearBorrador", err);
        setEstado("error");
      }
    } finally {
      creandoRef.current = false;
    }
  };

  // "vuelve al modal" (prompt): redirige de vuelta a la página actual (no al default
  // /admin/perfil) — el modal en sí no sobrevive el reload de un OAuth redirect completo, pero el
  // vendedor vuelve al Home con el Radar y puede reabrir el mismo sobre, ya reconectado.
  const reconectarGoogle = async () => {
    try {
      await connectGoogle(usuarioId, window.location.href, getPaisCode(pais));
    } catch {
      toast.error("Error al conectar con Google");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className={styles.dialog}>
      <ModalHeader toggle={onClose}>
        Escribir a {alerta?.empresaDisplay} · {lado === "agencia" ? "Agencia" : "Cliente"}
      </ModalHeader>
      <ModalBody>
        {estado === "reauth" ? (
          <div className={styles.reauth}>
            <p>Reconectá tu Google para crear borradores.</p>
            <p className={styles.reauthDetalle}>
              Tu conexión de Google todavía no tiene el permiso para crear borradores en Gmail — hace falta reconectar
              la cuenta una vez.
            </p>
          </div>
        ) : (
          <>
            {lado === "cliente" && hayAgenciaConEmail && !alerta?.escritoAgencia && (
              <div className={styles.recordatorioAgencia}>
                Todavía no le escribiste a la agencia. Conviene empezar por ahí.
              </div>
            )}
            {/* CAMBIO 1c (caso Samsung, 2026-09-27): si NINGÚN contacto de este lado tiene un
                email de formato válido, el aviso explica por qué "Abrir en Gmail" queda
                deshabilitado — el botón ya lo está solo, `seleccionados` no puede tener nada. */}
            {contactos.length > 0 && !hayAlgunEmailValido && (
              <div className={styles.avisoSinValidos}>
                Ningún contacto de este lado tiene un mail con formato válido — corregilo en el CRM antes de escribir.
              </div>
            )}
            <div className={styles.contactos}>
              {contactos.map((c) => (
                <label
                  key={c.usuarioId}
                  className={`${styles.contacto} ${!c.emailValido ? styles.contactoInvalido : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={c.emailValido && seleccionados.has(c.usuarioId)}
                    disabled={!c.emailValido}
                    onChange={() => toggleContacto(c.usuarioId)}
                  />
                  <span className={styles.contactoNombre}>
                    {c.nombre} {c.apellido}
                  </span>
                  {c.cargo && <span className={styles.contactoCargo}>{c.cargo}</span>}
                  {c.emailValido ? (
                    <span className={styles.contactoEmail}>{c.email}</span>
                  ) : (
                    <span className={styles.contactoEmailInvalido}>
                      Mail inválido ({c.email}). Corregilo en el CRM.
                    </span>
                  )}
                </label>
              ))}
            </div>
            <div className={styles.preview}>
              <div className={styles.previewAsunto}>
                <strong>Asunto:</strong> {asunto}
              </div>
              <div
                className={styles.previewCuerpo}
                style={{ fontFamily: "'Trebuchet MS',Helvetica,Arial,sans-serif", color: "#073763" }}
                dangerouslySetInnerHTML={{ __html: cuerpoHtml }}
              />
              <div className={styles.previewNota}>
                Tu firma de Gmail se agrega automáticamente al crear el borrador.
              </div>
            </div>
            {fotosDisponibles.length > 0 && (
              <div className={styles.adjuntosSeccion}>
                <div className={styles.adjuntosLabel}>Adjuntos</div>
                <div className={styles.fotos}>
                  {fotosVisibles.map((f) => (
                    <div key={f.empresaNorm} className={styles.fotoItem}>
                      <button
                        type="button"
                        className={styles.fotoQuitar}
                        onClick={() => quitarFoto(f.empresaNorm)}
                        aria-label={`Quitar foto de ${f.marca}`}
                        title="Quitar"
                      >
                        ×
                      </button>
                      <img src={f.dataUrlEstampada} alt={f.marca} className={styles.fotoThumb} />
                      <div className={styles.fotoMarca}>{f.marca}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {estado === "error" && (
              <div className={styles.errorMsg}>No se pudo crear el borrador. Probá de nuevo en un momento.</div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>
          Cancelar
        </Button>
        {estado === "reauth" ? (
          <Button color="primary" onClick={reconectarGoogle}>
            Reconectar Google
          </Button>
        ) : (
          <Button color="primary" onClick={crearBorrador} disabled={seleccionados.size === 0 || estado === "creando"}>
            {estado === "creando" ? "Creando…" : "Abrir en Gmail"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
